import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHeader } from "./fordon";
import { faqSchema } from "@/components/HubEditorial";
import { getMarketStats, type MarketRow, type MarketStats } from "@/lib/market-stats.functions";

const URL = "https://www.akhusbilfritid.se/prisstatistik-husbil";
const TITLE = "Prisstatistik husbil & husvagn – marknadspriser per fabrikat";
const DESC =
  "Marknadspriser för husbilar och husvagnar per segment, fabrikat och årsmodell — räknat på verkliga annonser, uppdaterat automatiskt.";

const FAQ = [
  {
    q: "Var kommer siffrorna ifrån?",
    a: "Från annonser vi själva följer dagligen. Varje rubrik tolkas till fabrikat, segment och årsmodell, priserna sammanställs som median och percentiler, och underlag med färre än tre objekt publiceras inte. Ingen siffra är uppskattad av oss i efterhand.",
  },
  {
    q: "Varför median och inte medelvärde?",
    a: "Ett enda extremt objekt — en integrerad Concorde bland halvintegrerade — drar medelvärdet flera hundra tusen. Medianen står still. Därför redovisar vi median samt spannet mellan 15:e och 85:e percentilen, som är den nivå de flesta affärer faktiskt görs på.",
  },
  {
    q: "Är annonspris samma som slutpris?",
    a: "Nej. På fritidsfordon ligger slutpriset historiskt omkring 10 procent under annonspriset hos handlare, och det är den justeringen vår värderingsmodell använder. Tabellerna nedan visar annonsnivå, alltså vad marknaden begär.",
  },
  {
    q: "Hur ofta uppdateras statistiken?",
    a: "Den räknas om vid varje sidvisning på det material som finns i databasen, och materialet uppdateras löpande när annonser tillkommer, ändras eller försvinner. Samma data kalibrerar värderingsverktyget varje natt.",
  },
];

export const Route = createFileRoute("/prisstatistik-husbil")({
  loader: async () => ({ stats: await getMarketStats() }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "prisstatistik husbil, marknadspris husvagn, vad kostar en husbil, husbilspriser 2026, prisutveckling husbil",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Prisstatistik husbil & husvagn — verkliga marknadsnivåer" },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "Marknadspriser husbil och husvagn i Sverige",
          description: DESC,
          url: URL,
          license: "https://www.akhusbilfritid.se/",
          creator: { "@type": "Organization", name: "AK Husbil & Fritid" },
          isAccessibleForFree: true,
          inLanguage: "sv-SE",
        }),
      },
    ],
  }),
  component: Page,
});

const kr = (v: number) => `${Math.round(v).toLocaleString("sv-SE")} kr`;

function Table({
  caption,
  rows,
  firstHeader,
}: {
  caption: string;
  rows: MarketRow[];
  firstHeader: string;
}) {
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto rounded-3xl border border-border bg-card">
      <table className="w-full min-w-[36rem] text-sm">
        <caption className="px-5 pt-5 text-left font-display text-lg">{caption}</caption>
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th scope="col" className="px-5 py-3">{firstHeader}</th>
            <th scope="col" className="px-5 py-3">Median</th>
            <th scope="col" className="px-5 py-3">Vanligt spann</th>
            <th scope="col" className="px-5 py-3">Median årsmodell</th>
            <th scope="col" className="px-5 py-3">Objekt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-border/60 last:border-0">
              <th scope="row" className="px-5 py-3 text-left font-medium">{r.label}</th>
              <td className="px-5 py-3 font-medium text-accent">{kr(r.medianPrice)}</td>
              <td className="px-5 py-3 text-muted-foreground">
                {kr(r.lowPrice)} – {kr(r.highPrice)}
              </td>
              <td className="px-5 py-3 text-muted-foreground">{r.medianYear}</td>
              <td className="px-5 py-3 text-muted-foreground">{r.n}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Page() {
  const { stats } = Route.useLoaderData();

  return (
    <>
      <PageHeader
        label="Data · Marknadspriser"
        title={
          <>
            Prisstatistik för <span className="font-italic-display text-accent">husbil och husvagn</span>
          </>
        }
        sub={
          stats.total
            ? `Räknat på ${stats.total} verkliga annonser vi följer dagligen. Median, spann och årsmodell per segment, fabrikat och årgång — samma underlag som kalibrerar vårt värderingsverktyg varje natt.`
            : "Marknadsnivåer per segment, fabrikat och årsmodell, räknat på verkliga annonser vi följer dagligen."
        }
      />

      {stats.categories.length > 0 && (
        <section className="container-edge pt-8 md:pt-12">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {stats.categories.map((c: MarketStats["categories"][number]) => (
              <div key={c.category} className="rounded-3xl border border-border bg-card p-6">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Median {c.category}
                </p>
                <p className="mt-2 font-display text-3xl leading-none text-accent">{kr(c.medianPrice)}</p>
                <p className="mt-2 text-sm text-muted-foreground">{c.n} annonser i underlaget</p>
              </div>
            ))}
            <div className="rounded-3xl border border-border bg-card p-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Metod</p>
              <p className="mt-2 font-display text-3xl leading-none">Median</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Spann = 15:e till 85:e percentilen. Inga grupper under tre objekt.
              </p>
            </div>
            <div className="rounded-3xl border border-border bg-card p-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Uppdaterad</p>
              <p className="mt-2 font-display text-3xl leading-none">
                {new Date(stats.updatedAt).toLocaleDateString("sv-SE")}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">Räknas om vid varje sidvisning.</p>
            </div>
          </div>
        </section>
      )}

      <section className="container-edge space-y-6 pt-12 md:pt-16">
        <Table caption="Marknadspris per segment" firstHeader="Segment" rows={stats.segments} />
        <Table caption="Marknadspris per fabrikat" firstHeader="Fabrikat" rows={stats.brands} />
        <Table caption="Marknadspris per årsmodell" firstHeader="Årsmodell" rows={stats.years} />
        {!stats.total && (
          <p className="rounded-3xl border border-border bg-card p-6 text-sm text-muted-foreground">
            Underlaget byggs upp löpande. Så snart tillräckligt många annonser är tolkade publiceras
            tabellerna här automatiskt — vi publicerar hellre inget än en siffra som vilar på tre objekt.
          </p>
        )}
      </section>

      <section className="container-edge pt-12 md:pt-16">
        <div className="prose-editorial max-w-3xl space-y-5 text-[0.975rem] leading-relaxed text-muted-foreground">
          <h2 className="font-display text-2xl text-foreground">Så ska tabellerna läsas</h2>
          <p>
            Priset på en husbil sätts inte av årsmodellen ensam. Två fordon från samma år kan skilja
            300 000 kronor på grund av chassi, planlösning, servicehistorik och fukt. Därför är medianen i
            tabellerna en utgångspunkt, inte en dom: den säger vad marknaden ligger på för den gruppen just
            nu, och spannet säger hur stor spridningen är inom gruppen. Är spannet brett innehåller
            segmentet allt från slitna objekt till nästan nya — då avgör skicket mer än årsmodellen.
          </p>
          <p>
            Segmentnivån är den mest stabila. Integrerade husbilar ligger konsekvent högst, följt av
            halvintegrerade med sänkbar säng, medan husbilar på skåpbilschassi (van och camper) håller sig i
            botten av prisbilden men tappar långsammast i procent. Husvagnar följer ett eget mönster: de
            skrivs av snabbare de första fem åren, men planar sedan ut på en nivå där ett välskött ekipage
            från 2012 kan vara värt nästan lika mycket som ett från 2015.
          </p>
          <p>
            Fabrikatnivån ska läsas med försiktighet. Att ett fabrikat visar hög median betyder oftast att
            de objekt vi följer inom fabrikatet är nyare eller större — inte att märket i sig är dyrare per
            meter. Jämför därför alltid median årsmodell i samma rad. Det är den kolumnen som avslöjar om en
            hög median beror på märket eller på åldern.
          </p>
          <h2 className="font-display text-2xl text-foreground">Varför annonspris inte är slutpris</h2>
          <p>
            Statistiken visar vad marknaden begär, inte vad den får. På fritidsfordon hos handlare ligger
            slutpriset historiskt omkring tio procent under annonspriset, och skillnaden är större vintertid
            än i mars–juni när efterfrågan toppar. Vår värderingsmodell räknar därför ner annonsnivån innan
            den jämför, och samma justering ligger bakom det spann du får i värderingsverktyget.
          </p>
          <p>
            Vill du gå från marknadsnivå till ditt eget fordon behöver du väga in mil, fukt, däckens ålder
            och servicehistorik. Det gör{" "}
            <Link to="/vardera-husbil" className="font-medium text-accent underline decoration-accent/40">
              värderingsverktyget
            </Link>
            , som visar varje multiplikator öppet och dessutom jämför mot verkliga objekt ur vårt lager. Ska
            du sälja är{" "}
            <Link to="/salja-husbil" className="font-medium text-accent underline decoration-accent/40">
              säljguiden
            </Link>{" "}
            nästa steg — den går igenom skillnaden mellan privat annons, förmedling och inbyte i kronor.
          </p>
          <p>
            Den här sidan visar nivån just nu. Vill du i stället se om priserna rör sig uppåt eller nedåt
            finns{" "}
            <Link to="/prisindex" className="font-medium text-accent underline decoration-accent/40">
              AK Prisindex
            </Link>
            , där varje månad sparas som en låst mätpunkt med medianpris, spann och förändring i procent —
            plus vad det kostar att utrusta fordonet, räknat på våra egna butikspriser.
          </p>
          <h2 className="font-display text-2xl text-foreground">Vanliga frågor</h2>
          <dl className="space-y-4">
            {FAQ.map((f) => (
              <div key={f.q}>
                <dt className="font-medium text-foreground">{f.q}</dt>
                <dd className="mt-1">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </>
  );
}
